/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { CSSProperties, useEffect, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { useParams, useNavigate } from "react-router-dom";
import {
  useUploadDocument,
  useSaveImportedFarmers,
  useGetImportedFarmersById,
  useGetNonContractFarmerLoanById,
} from "../../api/customer";
import {
  Button,
  Card,
  Form,
  Image,
  message,
  Table,
  Tabs,
  TabsProps,
  Upload,
  Modal,
  List,
} from "antd";
import img from "../../assets/images/user.png";
import female from "../../assets/images/user-female.png";
import pdfIcon from "../../assets/images/pdf.png";
import {
  GoogleMap,
  InfoWindow,
  MarkerF,
  useLoadScript,
} from "@react-google-maps/api";
import { FarmerCard } from "../../components/Map";
import {
  DownloadOutlined,
  UploadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import { INonContractFarmer } from "../../models/nonContractFarmerModel";

export interface IAddress {
  landCode: string;
  landSize: number;
  latitude: number;
  longitude: number;
  region: string;
  zone: string;
  woreda: string;
  kebele: string;
  unionName: string;
  primaryCooperativeName: string;
}

const label: CSSProperties = {
  color: "#128807",
  fontWeight: 500,
  fontSize: 16,
};
const value: CSSProperties = {
  color: "#8590A3",
  fontWeight: 500,
  fontSize: 14,
};

const NonContractFarmerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const customerId = id ? parseInt(id, 10) : 0;
  const {
    data: customer,
    refetch,
    isLoading: customerLoading,
  } = useGetImportedFarmersById(id ? parseInt(id, 10) : 0);
  const {
    mutateAsync: uploadDocument,
    isSuccess,
    isPending,
  } = useUploadDocument();

  const {
    mutateAsync: saveImportedFarmers,
    isPending: isSaving,
    isSuccess: isSavingSuccess,
  } = useSaveImportedFarmers();
  const { data: loanData, isLoading: loanLoading } =
    useGetNonContractFarmerLoanById(customerId);
  const [activeTab, setActiveTab] = useState("personal");
  const { user } = useAuth() as AuthContextValue;
  const [role, setRole] = useState<"ROLE_KFT_SP_ADMIN" | undefined>();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [form] = Form.useForm();

  const [fileList, setFileList] = useState<any[]>([]);
  const farmer = customer?.data;

  // Google Maps setup (using placeholder coordinates since latitude/longitude not provided)
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });
  const defaultCenter = { lat: 8.9806, lng: 38.7578 }; // Default to Addis Ababa, Ethiopia

  // Document table columns
  const columns = [
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      render: (text: string) => text?.split(".")[0],
    },
    {
      title: "File Type",
      dataIndex: "fileType",
      key: "fileType",
    },
    {
      title: "Preview",
      dataIndex: "preview",
      key: "preview",
      render: (text: any, record: any) =>
        record.fileType?.includes("pdf") ? (
          <Image preview={false} src={pdfIcon} width={50} height={50} />
        ) : (
          <Image
            src={
              record.filePath
                ? `http://your-api-base-url${record.filePath}`
                : img
            }
            height={50}
            width={50}
          />
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (text: any, record: any) => (
        <Button
          type="link"
          icon={<DownloadOutlined />}
          href={
            record.filePath ? `http://your-api-base-url${record.filePath}` : "#"
          }
          download={`${farmer?.fullName}-${record.fileName?.split(".")[0]}`}
        />
      ),
    },
  ];

  // Mock document data from file paths since documents is null
  const documentData = farmer
    ? [
        farmer.idFilePath && {
          fileName: "ID Document",
          fileType: "image/jpeg",
          filePath: farmer.idFilePath,
        },
        farmer.tradeLicenceFilePath && {
          fileName: "Trade Licence",
          fileType: "image/jpeg",
          filePath: farmer.tradeLicenceFilePath,
        },
      ].filter(Boolean)
    : [];

  // Handle file upload
  const handleUpload = async ({ file, onSuccess, onError }: any) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("customerId", id || "");
      await uploadDocument(formData);
      onSuccess("File uploaded successfully");
      refetch();
    } catch (error) {
      onError("File upload failed");
      message.error("Failed to upload file");
    }
  };

  const handleUpdate = async () => {
    if (!farmer) {
      message.error("No farmer data available.");
      return;
    }

    try {
      const payload: INonContractFarmer = {
        bankAccount: farmer.bankAccount,
        bankAccountName: farmer.bankAccountName,
        bankId: farmer.bankId,
        birthDate: farmer.birthDate,
        createdBy: user?.userId,
        customerFullName: farmer.fullName,
        email: farmer.email ?? null,
        farmersExtraDTO: {
          annualProfit: farmer?.annualProfit,
          annualSales: farmer?.annualSales,
          associationType: farmer?.associationType,
          businessAddressHouseNumber: farmer?.businessAddressHouseNumber,
          businessAddressRegion: farmer?.businessAddressRegion,
          businessAddressWoreda: farmer?.businessAddressWoreda,
          businessAddressZoneSubcity: farmer?.businessAddressZoneSubcity,
          businessLevel: farmer?.businessLevel,
          businessSector: farmer?.businessSector,
          businessStartYear: farmer?.businessStartYear,
          currentCapital: farmer?.currentCapital,
          currentNumberOfEmployees: farmer?.currentNumberOfEmployees,
          customerId: farmer.customerId,
          educationLevel: farmer?.educationLevel,
          emergencyContactName: farmer?.emergencyContactName,
          emergencyContactPhoneNumber: farmer?.emergencyContactPhoneNumber,
          id: farmer?.id,
          idFilePath: fileList[0]?.originFileObj
            ? URL.createObjectURL(fileList[0].originFileObj)
            : undefined,
          initialSourceOfCapital: farmer?.initialSourceOfCapital,
          lineOfBusiness: farmer?.lineOfBusiness,
          personalAddressHouseNumber: farmer?.personalAddressHouseNumber,
          personalAddressRegion: farmer?.personalAddressRegion,
          personalAddressWoreda: farmer?.personalAddressWoreda,
          personalAddressZoneSubcity: farmer?.personalAddressZoneSubcity,
          startingCapital: farmer?.startingCapital,
          startingNumberOfEmployees: farmer?.startingNumberOfEmployees,
          tradeLicenceFilePath: fileList[1]?.originFileObj
            ? URL.createObjectURL(fileList[1].originFileObj)
            : undefined,
        },
        gender: farmer.gender,
        houseNo: farmer.houseNo,
        idNo: farmer.idNo,
        idType: farmer.idType,
        kebele: farmer.kebele,
        maritalStatus: farmer.maritalStatus,
        multipurposeCooperativeId: farmer.multipurposeCooperativeId,
        phoneNumber: farmer.phoneNumber,
        spouseTinNo: farmer.spouseTinNo ?? null,
        tinNo: farmer.tinNo ?? null,
        uuid: farmer.uuid || crypto.randomUUID(),
        woredaId: farmer.woredaId,
        productName: farmer.productName,
      };

      await saveImportedFarmers([payload]);

      message.success("Farmer saved successfully.");
    } catch (error) {
      console.error("Save failed:", error);
      message.error("Failed to save farmer.");
    }
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload: INonContractFarmer = {
        ...farmer,
        customerFullName: values.fullName,
        gender: values.gender,
        phoneNumber: values.phoneNumber,
        email: values.email,
        maritalStatus: values.maritalStatus,
        educationLevel: values.educationLevel,
        idType: values.idType,
        idNo: values.idNo,
        emergencyContactName: values.emergencyContactName,
        emergencyContactPhoneNumber: values.emergencyContactPhoneNumber,
        businessSector: values.businessSector,
        lineOfBusiness: values.lineOfBusiness,
        businessStartYear: values.businessStartYear,
        businessLevel: values.businessLevel,
        farmersExtraDTO: {
          ...farmer.farmersExtraDTO,
          educationLevel: values.educationLevel,
          emergencyContactName: values.emergencyContactName,
          emergencyContactPhoneNumber: values.emergencyContactPhoneNumber,
          businessSector: values.businessSector,
          lineOfBusiness: values.lineOfBusiness,
          businessStartYear: values.businessStartYear,
          businessLevel: values.businessLevel,
        },
        createdBy: user?.userId,
      };

      await saveImportedFarmers([payload]);
      message.success("Farmer updated successfully");
      setEditModalVisible(false);
      refetch();
    } catch (error) {
      console.error("Update failed:", error);
      message.error("Failed to update farmer.");
    }
  };

  // Tabs configuration
  const tabItems = [
    {
      key: "personal",
      label: "FARMER DETAILS",
      children: (
        <div className="flex w-full mb-6 gap-6">
          {/* Left: Farmer photo */}
          <div className="bg-white rounded-lg shadow-md p-4 flex items-center justify-center w-1/3">
            <Image
              src={farmer?.gender === "MALE" ? img : female}
              height={150}
              width={150}
              className="rounded-full"
              alt="Farmer Profile"
            />
          </div>

          {/* Right: Farmer details */}
          <div className="bg-white rounded-lg shadow-md p-6 w-2/3">
            <div className="flex justify-between items-center bg-gray-200 p-4 rounded-lg font-medium text-lg mb-4">
              <span>Farmer Detail</span>
              {/* <EditOutlined
              className="cursor-pointer text-blue-600"
              onClick={showEditModal}
            /> */}
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm">
              {[
                { label: "Full Name", value: farmer?.fullName },
                { label: "Gender", value: farmer?.gender },
                { label: "Phone Number", value: farmer?.phoneNumber },
                { label: "Customer ID", value: farmer?.customerId },
                { label: "TIN", value: farmer?.tinNumber },
                { label: "Marital Status", value: farmer?.maritalStatus },
                { label: "Education Level", value: farmer?.educationLevel },
                { label: "ID Type", value: farmer?.idType },
                { label: "Product Name", value: farmer?.productName },
                {
                  label: "Identification Number",
                  value: farmer?.identificationNumber,
                },
                {
                  label: "Emergency Contact Name",
                  value: farmer?.emergencyContactName,
                },
                {
                  label: "Emergency Contact Phone",
                  value: farmer?.emergencyContactPhoneNumber,
                },
                { label: "Business Sector", value: farmer?.businessSector },
                { label: "Line of Business", value: farmer?.lineOfBusiness },
                {
                  label: "Business Start Year",
                  value: farmer?.businessStartYear,
                },
                { label: "Business Level", value: farmer?.businessLevel },
                { label: "House Number", value: farmer?.houseNumber },
                { label: "Region", value: farmer?.businessAddressRegion },
                { label: "Zone", value: farmer?.businessAddressZoneSubcity },
                { label: "Woreda", value: farmer?.businessAddressWoreda },
                { label: "Kebele", value: farmer?.businessAddressHouseNumber },
                {
                  label: "Current No of Employees",
                  value: farmer?.currentNumberOfEmployees,
                },
                { label: "Association Type", value: farmer?.associationType },
                {
                  label: "Starting No of Employees",
                  value: farmer?.startingNumberOfEmployees,
                },
                { label: "Registration Date", value: farmer?.registrationDate },
                {
                  label: "Intial Source of Capital",
                  value: farmer?.initialSourceOfCapital.toLocaleString(
                    "en-ET",
                    {
                      style: "currency",
                      currency: "ETB",
                    }
                  ),
                },
                {
                  label: "Starting Capital",
                  value: farmer?.startingCapital.toLocaleString("en-ET", {
                    style: "currency",
                    currency: "ETB",
                  }),
                },
                {
                  label: "Current capital",
                  value: farmer?.currentCapital.toLocaleString("en-ET", {
                    style: "currency",
                    currency: "ETB",
                  }),
                },
                {
                  label: "Annual Sales",
                  value: farmer?.annualSales.toLocaleString("en-ET", {
                    style: "currency",
                    currency: "ETB",
                  }),
                },
                {
                  label: "Annual Profit",
                  value: farmer?.annualProfit.toLocaleString("en-ET", {
                    style: "currency",
                    currency: "ETB",
                  }),
                },
              ].map((item, index) => (
                <div key={index}>
                  <div style={label}>{item.label}</div>
                  <div style={value}>{item.value || "N/A"}</div>
                </div>
              ))}
            </div>

            <Button
              type="primary"
              loading={isSaving}
              onClick={handleUpdate}
              className="mt-6"
            >
              Save Changes
            </Button>
          </div>
        </div>
      ),
    },
    // {
    //   key: "documents",
    //   label: "Documents",
    //   children: (
    //     <Card>
    //       <Upload
    //         customRequest={handleUpload}
    //         fileList={fileList}
    //         onChange={({ fileList }) => setFileList(fileList)}
    //         accept=".pdf,.jpg,.jpeg,.png"
    //       >
    //         <Button icon={<UploadOutlined />}>Upload Document</Button>
    //       </Upload>
    //       <Table
    //         columns={columns}
    //         dataSource={documentData}
    //         rowKey="fileName"
    //         pagination={false}
    //         style={{ marginTop: 16 }}
    //         loading={customerLoading}
    //       />
    //     </Card>
    //   ),
    // },
  ];
  useEffect(() => {
    if (user?.roleName === "ROLE_KFT_SP_ADMIN") {
      setRole("ROLE_KFT_SP_ADMIN");
    } else {
      setRole(undefined);
    }
  }, [user]);

  // Refresh documents on successful upload
  useEffect(() => {
    if (isSuccess) {
      message.success("Document uploaded successfully");
      setFileList([]);
      refetch();
    }
  }, [isSuccess, refetch]);

  return (
    <div className="p-4">
      <Tabs
        style={{
          background: "white",
          fontWeight: 700,
          paddingLeft: 20,
        }}
        className="text-black"
        defaultActiveKey="1"
        items={tabItems}
        onChange={(key) => setActiveTab(key)}
      />

      {/* Edit modal */}

      <Modal
        open={editModalVisible}
        title="Edit Farmer Details"
        onCancel={() => setEditModalVisible(false)}
        onOk={handleEditSubmit}
        confirmLoading={isSaving}
        okText="Save"
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            label="Full Name"
            name="fullName"
            rules={[{ required: true }]}
          >
            <input className="ant-input" />
          </Form.Item>
          <Form.Item label="Gender" name="gender" rules={[{ required: true }]}>
            <input className="ant-input" />
          </Form.Item>
          <Form.Item label="Phone Number" name="phoneNumber">
            <input className="ant-input" />
          </Form.Item>
          <Form.Item label="Email" name="email">
            <input className="ant-input" />
          </Form.Item>
          <Form.Item label="Marital Status" name="maritalStatus">
            <input className="ant-input" />
          </Form.Item>
          <Form.Item label="Education Level" name="educationLevel">
            <input className="ant-input" />
          </Form.Item>
          <Form.Item label="ID Type" name="idType">
            <input className="ant-input" />
          </Form.Item>
          <Form.Item label="ID Number" name="idNo">
            <input className="ant-input" />
          </Form.Item>
          <Form.Item label="Emergency Contact Name" name="emergencyContactName">
            <input className="ant-input" />
          </Form.Item>
          <Form.Item
            label="Emergency Contact Phone"
            name="emergencyContactPhoneNumber"
          >
            <input className="ant-input" />
          </Form.Item>
          <Form.Item label="Business Sector" name="businessSector">
            <input className="ant-input" />
          </Form.Item>
          <Form.Item label="Line of Business" name="lineOfBusiness">
            <input className="ant-input" />
          </Form.Item>
          <Form.Item label="Business Start Year" name="businessStartYear">
            <input className="ant-input" />
          </Form.Item>
          <Form.Item label="Business Level" name="businessLevel">
            <input className="ant-input" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export const FarmLandCard = ({
  region,
  zone,
  woreda,
  kebele,
  landSize,
}: {
  region: string;
  zone: string;
  woreda: string;
  kebele: string;
  landSize: number;
}) => {
  return (
    <div className="w-[300px] flex flex-row gap-2">
      <div className="flex flex-col w-[600px] gap-2">
        <div className="flex gap-8">
          <div className="px-2 w-full flex flex-col gap-4 text-lg">
            <div className="flex justify-between border-b-2 border-dashed border-gray-400">
              <div className="font-semibold text-gray-400">Region</div>
              <div className="font-bold">{region}</div>
            </div>
            <div className="flex justify-between border-b-2 border-dashed border-gray-400">
              <div className="font-semibold text-gray-400">Zone</div>
              <div className="font-bold">{zone}</div>
            </div>
            <div className="flex justify-between border-b-2 border-dashed border-gray-400">
              <div className="font-semibold text-gray-400">Woreda</div>
              <div className="font-bold">{woreda}</div>
            </div>
            <div className="flex justify-between border-b-2 border-dashed border-gray-400">
              <div className="font-semibold text-gray-400">Kebele</div>
              <div className="font-bold">{kebele}</div>
            </div>
            <div className="flex justify-between border-b-2 border-dashed border-gray-400">
              <div className="font-semibold text-gray-400">Land Size</div>
              <div className="font-bold">{landSize} ha</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithAuth(NonContractFarmerDetailPage);
